import type { Space } from '@cocoonspace/sdk-js/types/space'
import type { Zone } from '@cocoonspace/sdk-js/types/zone'
import { bookingConfig } from '@cocoonspace/shared/config/booking-config'
import { searchConfig } from '@cocoonspace/shared/config/search-config'
import { useMediaQuery } from '@cocoonspace/shared/hooks/use-media-query.hook'
import { getCleanObject } from '@cocoonspace/shared/utils/get-clean-object'
import { LayoutGroup, motion } from 'framer-motion'
import { useTranslation } from 'next-i18next'
import { usePlausible } from 'next-plausible'
import { useRouter } from 'next/router'
import { useMemo, useState } from 'react'
import { Button } from '~/components/ui/button'
import { mapEmitter } from '~/domains/search/emitters/search.emitters'
import { useSearchSpaces } from '~/domains/search/hooks/use-search-spaces.hook'
import { cn } from '~/lib/utils'
import { useTracking } from '~/providers/tracking-provider'
import { twTheme } from '~/theme'
import type { QueryFilters } from '../filters/use-search-filters.hook'
import SpaceCard from '../space-card'
import { SpacesGroupHeader } from './spaces-group-header'

export const SpacesGroup = ({
	label,
	items,
	isAlternative = false,
	timeSlot,
}: {
	label?: string
	items: Space[]
	timeSlot: {
		day: QueryFilters['day']
		start: QueryFilters['start']
		end: QueryFilters['end']
	}
	isAlternative?: boolean
}) => {
	const router = useRouter()
	const { t } = useTranslation()
	const { track } = useTracking()
	const plausible = usePlausible()

	const isTablet = useMediaQuery(`(min-width: ${twTheme.theme.screens.sm})`)
	const isDesktop = useMediaQuery(`(min-width: ${twTheme.theme.screens.md})`)

	const {
		spaceType,
		filters,
		sortBy,
		sortOrder,
		isMapVisible,
		activeAlternative,
		spacesListQuery,
	} = useSearchSpaces()

	const [showAll, setShowAll] = useState(() => !isAlternative)

	const alternativeColSize = useMemo(() => {
		if (isDesktop) return isMapVisible ? 2 : 3
		else if (isTablet) return isMapVisible ? 1 : 2
		else return 1
	}, [isDesktop, isTablet, isMapVisible])

	return (
		<div className='mb-8'>
			{(activeAlternative !== label || label) && (
				<SpacesGroupHeader
					label={label}
					items={items}
					isAlternative={isAlternative}
					timeSlot={timeSlot}
				/>
			)}

			<div
				data-testid='spaces-layout-group'
				className={cn(
					'grid grid-cols-1 gap-4 pt-[2px]',
					isMapVisible
						? 'lg:grid-cols-2'
						: 'md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4',
				)}
			>
				{items
					?.filter((_, index) => {
						if (!isAlternative) return true
						if (showAll) return true

						return index < alternativeColSize
					})
					.sort((a, b) => {
						if (sortBy === 'capacity') {
							return sortOrder === 'asc'
								? a.capacity - b.capacity
								: b.capacity - a.capacity
						}

						return 0
					})
					.map((space) => (
						<LayoutGroup key={space.id}>
							<motion.div
								key={space.id}
								id={space.id}
								data-testid={`space-${space.id}`}
								layout={isDesktop}
							>
								<SpaceCard
									spaceType={spaceType}
									className={cn({
										'shadow-[0_0_0_2px_#ff4338]': filters?.text === space.title,
									})}
									space={space}
									href={{
										pathname:
											spaceType === 'desk'
												? `/desks/${space.zone}/${space.slug}`
												: `/${space.zone}/${space.slug}`,
										query: getCleanObject({
											day: timeSlot.day,
											start: timeSlot.start?.replace(':', '-'),
											end: timeSlot.end?.replace(':', '-'),
										}),
									}}
									showDistance={!!filters?.text?.length}
									onMouseOver={() => mapEmitter.emit('map.cardOver', space)}
									onMouseLeave={() => mapEmitter.emit('map.cardOut')}
									onClick={() => {
										plausible('search_space_card_click', {
											props: {
												from: 'list',
												type: isAlternative ? 'alternative' : 'main',
												results: spacesListQuery.data?.data.length || 0,
												filters,
												activeAlternative,
											},
										})

										track.searchSpaceCardClick({
											type: isAlternative ? 'alternative' : 'main',
											resultCount: spacesListQuery.data?.data.length,
											capacities: bookingConfig.capacities,
											zoom: searchConfig.zones[router.query.zone as Zone].zoom
												.search,
											filters,
										})
									}}
								/>
							</motion.div>
						</LayoutGroup>
					))}
			</div>

			{isAlternative && items?.length && items?.length > alternativeColSize && (
				<div className='mt-8 w-full text-center'>
					<Button
						variant='outline'
						onClick={() => setShowAll((showAll) => !showAll)}
					>
						{showAll
							? t('search:actions.displayLessCocoons')
							: t('search:actions.displayCocoons', { count: items?.length })}
					</Button>
				</div>
			)}
		</div>
	)
}
